import {
  useContext,
  useEffect,
  useMemo,
  useState,
  type WheelEvent,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  MAX_FEE_ADJUSTMENT_REASON_LENGTH,
  type AdjustSection12PaymentRequestInput,
  type Section12PaymentRequestWithDetails,
} from "@thalamos/aspire-common";

import { ManagedLocationsContext } from "../../Contexts.js";
import {
  formatCurrency,
  getItemTypeLabel,
} from "../Section12PaymentRequests/payment-request-helpers.js";
import {
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

function blurNumberInputOnWheel(event: WheelEvent<HTMLInputElement>) {
  event.currentTarget.blur();
}

interface AdjustPaymentRequestDialogProps {
  request: Section12PaymentRequestWithDetails | null;
  onClose: () => void;
  onSuccess: () => void;
  adjustPaymentRequest: (
    paymentRequestId: string,
    body: AdjustSection12PaymentRequestInput,
  ) => Promise<{ status: number; data: unknown }>;
}

type LocalAuthorityOption = {
  id: string;
  name: string;
};

export function AdjustPaymentRequestDialog({
  request,
  onClose,
  onSuccess,
  adjustPaymentRequest,
}: AdjustPaymentRequestDialogProps) {
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [amountsPence, setAmountsPence] = useState<Record<string, number>>({});
  const [mileageRates, setMileageRates] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { managedLocations } = useContext(ManagedLocationsContext);
  const localAuthorityOptions = useMemo<LocalAuthorityOption[]>(
    () =>
      managedLocations
        .filter(
          (loc) => loc.type === "localAuthority" && loc.status === "active",
        )
        .map((loc) => ({ id: loc.id, name: loc.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [managedLocations],
  );

  const [currentLocalAuthority, setCurrentLocalAuthority] =
    useState<LocalAuthorityOption | null>(null);
  const [selectedLocalAuthority, setSelectedLocalAuthority] =
    useState<LocalAuthorityOption | null>(null);
  const [initialSelectedLaId, setInitialSelectedLaId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (request) {
      const initial: Record<string, string> = {};
      const initialPence: Record<string, number> = {};
      const initialRates: Record<string, string> = {};
      for (const item of request.items) {
        if (
          item.itemType === "mileage" &&
          item.mileageDistanceMiles != null &&
          item.mileageDistanceMiles > 0
        ) {
          const ratePerMile =
            item.amountPence / item.mileageDistanceMiles / 100;
          initialRates[item.id] = ratePerMile.toFixed(2);
        }
        initialPence[item.id] = item.amountPence;
        initial[item.id] = (item.amountPence / 100).toFixed(2);
      }
      setAmounts(initial);
      setAmountsPence(initialPence);
      setMileageRates(initialRates);
      setReason("");
      // Two distinct pieces of LA state:
      //   - `currentLocalAuthority` tracks the *override* (latestLocalAuthority*).
      //     NULL = no override. Used by `localAuthorityChanged` so submitting an
      //     unchanged form never creates a no-op override row.
      //   - `selectedLocalAuthority` seeds the picker with the *effective* LA
      //     (override-else-snapshot) so the admin sees current state before
      //     deciding to change it.
      const initialOverride = request.latestLocalAuthorityId
        ? {
            id: request.latestLocalAuthorityId,
            name: request.latestLocalAuthorityName ?? "",
          }
        : null;
      const initialDisplayed = request.localAuthority
        ? {
            id: request.localAuthority.id,
            name: request.localAuthority.name,
          }
        : initialOverride;
      setCurrentLocalAuthority(initialOverride);
      setSelectedLocalAuthority(initialDisplayed);
      setInitialSelectedLaId(initialDisplayed?.id ?? null);
    }
  }, [request]);

  const handleClose = () => {
    setAmounts({});
    setAmountsPence({});
    setMileageRates({});
    setReason("");
    setCurrentLocalAuthority(null);
    setSelectedLocalAuthority(null);
    setInitialSelectedLaId(null);
    onClose();
  };

  // Two conditions must both hold for the LA edit to count as a real change:
  //   1. The admin has touched the picker (selected differs from what we
  //      seeded it with on open).
  //   2. The resulting value differs from the override stored on the row.
  // Without (1), opening on a row with no override would treat the seeded
  // snapshot as a pending change. Without (2), picking the same LA as the
  // existing override would create a noisy no-op audit entry.
  const selectedLaId = selectedLocalAuthority?.id ?? null;
  const overrideLaId = currentLocalAuthority?.id ?? null;
  const localAuthorityChanged =
    !!request &&
    selectedLaId !== initialSelectedLaId &&
    selectedLaId !== overrideLaId;

  const hasFeeChanges =
    !!request &&
    request.items.some((item) => {
      const pence = amountsPence[item.id];
      return pence != null && !isNaN(pence) && pence !== item.amountPence;
    });

  const isValid =
    (hasFeeChanges || localAuthorityChanged) && reason.trim().length > 0;

  const handleSubmit = async () => {
    if (!request || !isValid) return;

    const items = request.items
      .map((item) => {
        const pence = amountsPence[item.id];
        if (pence == null || isNaN(pence) || pence === item.amountPence)
          return null;
        return { itemId: item.id, newAmountPence: pence };
      })
      .filter(
        (entry): entry is { itemId: string; newAmountPence: number } =>
          entry !== null,
      );

    setIsSubmitting(true);
    try {
      const body: AdjustSection12PaymentRequestInput = {
        reason: reason.trim(),
        ...(items.length > 0 ? { items } : {}),
        ...(localAuthorityChanged
          ? { localAuthorityId: selectedLocalAuthority?.id ?? null }
          : {}),
      };
      const response = await adjustPaymentRequest(request.id, body);
      if (response.status < 200 || response.status >= 400) {
        throw new Error("Something went wrong. Please try again.");
      }
      renderSuccessToast({
        message: "Payment request updated.",
      });
      onSuccess();
    } catch {
      renderErrorToast({
        message: "Failed to update payment request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={request !== null}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Adjust Payment Request</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Adjust the local authority and/or fee values for{" "}
          <strong>{request?.doctor?.name ?? "this doctor"}</strong>
          {"'s"} payment request.
        </DialogContentText>

        {request && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Local Authority
              </Typography>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={localAuthorityOptions}
                  value={selectedLocalAuthority}
                  disableClearable={currentLocalAuthority === null}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) => option.name}
                  onChange={(_, newValue) => {
                    setSelectedLocalAuthority(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Local Authority"
                      placeholder="Select a local authority"
                    />
                  )}
                  disabled={isSubmitting}
                />
                {selectedLocalAuthority && currentLocalAuthority && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedLocalAuthority(null);
                    }}
                    disabled={isSubmitting}
                    sx={{ flexShrink: 0, mt: 0.25, whiteSpace: "nowrap" }}
                  >
                    Clear override
                  </Button>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                {(() => {
                  const overrideId = currentLocalAuthority?.id ?? null;
                  const selected = selectedLocalAuthority;
                  const selectedId = selected?.id ?? null;
                  const snapshotName = request.localAuthorityName ?? "Unknown";
                  if (selected === null) {
                    return "No LA will be set. The CSV will use the patient's current GP at download time.";
                  }
                  if (overrideId === null) {
                    if (selectedId === (request.localAuthority?.id ?? null)) {
                      return "Resolved from the patient's GP at request creation. Pick a value here to pin the LA, or clear to fall back to the patient's current GP.";
                    }
                    return `Will pin the LA to ${selected.name}. Original snapshot: ${snapshotName}.`;
                  }
                  if (selectedId === overrideId) {
                    return `Manually overridden. Original snapshot: ${snapshotName}.`;
                  }
                  return `Override will be updated to ${selected.name}. Original snapshot: ${snapshotName}.`;
                })()}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Current Value</TableCell>
                    <TableCell align="right">New Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {request.items.map((item) => {
                    const isMileage =
                      item.itemType === "mileage" &&
                      item.mileageDistanceMiles != null &&
                      item.mileageDistanceMiles > 0;

                    const newPence = amountsPence[item.id] ?? item.amountPence;
                    const changed =
                      !isNaN(newPence) && newPence !== item.amountPence;

                    return (
                      <TableRow key={item.id}>
                        <TableCell>{getItemTypeLabel(item)}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.amountPence)}
                        </TableCell>
                        <TableCell align="right">
                          {isMileage ? (
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  gap: 0.5,
                                }}
                              >
                                {item.mileageDistanceMiles} mi &times;
                                <TextField
                                  size="small"
                                  type="number"
                                  value={mileageRates[item.id] ?? ""}
                                  onChange={(e) => {
                                    const rate = e.target.value;
                                    setMileageRates((prev) => ({
                                      ...prev,
                                      [item.id]: rate,
                                    }));
                                    const parsedRate = parseFloat(rate);
                                    if (
                                      !isNaN(parsedRate) &&
                                      item.mileageDistanceMiles != null
                                    ) {
                                      const pence = Math.round(
                                        item.mileageDistanceMiles *
                                          parsedRate *
                                          100,
                                      );
                                      setAmountsPence((prev) => ({
                                        ...prev,
                                        [item.id]: pence,
                                      }));
                                      setAmounts((prev) => ({
                                        ...prev,
                                        [item.id]: (pence / 100).toFixed(2),
                                      }));
                                    }
                                  }}
                                  slotProps={{
                                    input: {
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          £
                                        </InputAdornment>
                                      ),
                                      inputProps: {
                                        "aria-label": `Rate per mile for ${getItemTypeLabel(item)}`,
                                        min: 0,
                                        step: "0.01",
                                        style: { textAlign: "right" },
                                        onWheel: blurNumberInputOnWheel,
                                      },
                                    },
                                  }}
                                  sx={{ width: 100 }}
                                />
                                = {formatCurrency(newPence)}
                              </Typography>
                              {changed && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    textAlign: "right",
                                    color:
                                      newPence > item.amountPence
                                        ? "success.main"
                                        : "error.main",
                                    mt: 0.5,
                                  }}
                                >
                                  {newPence > item.amountPence ? "+" : ""}
                                  {formatCurrency(newPence - item.amountPence)}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <>
                              <TextField
                                size="small"
                                type="number"
                                value={amounts[item.id] ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setAmounts((prev) => ({
                                    ...prev,
                                    [item.id]: value,
                                  }));
                                  setAmountsPence((prev) => ({
                                    ...prev,
                                    [item.id]: Math.round(
                                      parseFloat(value) * 100,
                                    ),
                                  }));
                                }}
                                slotProps={{
                                  input: {
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        £
                                      </InputAdornment>
                                    ),
                                    inputProps: {
                                      "aria-label": `New value for ${getItemTypeLabel(item)}`,
                                      min: 0,
                                      step: "0.01",
                                      style: { textAlign: "right" },
                                      onWheel: blurNumberInputOnWheel,
                                    },
                                  },
                                }}
                                sx={{ width: 140 }}
                              />
                              {changed && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color:
                                      newPence > item.amountPence
                                        ? "success.main"
                                        : "error.main",
                                    mt: 0.5,
                                  }}
                                >
                                  {newPence > item.amountPence ? "+" : ""}
                                  {formatCurrency(newPence - item.amountPence)}
                                </Typography>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              label="Reason for change"
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              required
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              slotProps={{
                htmlInput: { maxLength: MAX_FEE_ADJUSTMENT_REASON_LENGTH },
              }}
              helperText={`${String(reason.length)}/${String(MAX_FEE_ADJUSTMENT_REASON_LENGTH)}`}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            void handleSubmit();
          }}
          variant="contained"
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? "Adjusting..." : "Adjust Payment Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
